/**
 * Hook for fetching and caching exchange rates from the backend.
 * Exchange rates are stored in the database and refreshed every 12 hours.
 */

import { useQuery } from '@tanstack/react-query';
import { exchangeRatesApi, type ExchangeRatesApiResponse } from '../api/transactionApi';
import type { SupportedCurrency } from '../types';

/**
 * Fetch all exchange rates from the backend.
 * These rates are cached in the database and refreshed every 12 hours.
 */
async function fetchExchangeRates(): Promise<ExchangeRatesApiResponse['data']> {
  const response = await exchangeRatesApi.getAll();
  if (!response.success) {
    throw new Error('Failed to fetch exchange rates');
  }
  return response.data;
}

/**
 * Hook to get all exchange rates from the database.
 * Returns rates that are refreshed every 12 hours from external API.
 *
 * @returns Query result with rates, updatedAt timestamp, and supported currencies
 */
export function useExchangeRates() {
  const query = useQuery({
    queryKey: ['exchangeRates'],
    queryFn: fetchExchangeRates,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return {
    rates: query.data?.rates ?? null,
    updatedAt: query.data?.updatedAt ? new Date(query.data.updatedAt) : null,
    supportedCurrencies: query.data?.supportedCurrencies ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Get the exchange rate for a specific currency.
 * Returns the rate that converts BRL to the target currency (1 BRL = X target).
 *
 * @param rates - All exchange rates from useExchangeRates
 * @param currency - Target currency
 * @returns Exchange rate or null if not available
 */
export function getExchangeRate(
  rates: Record<SupportedCurrency, number> | null,
  currency: SupportedCurrency
): number | null {
  if (!rates) return null;
  return rates[currency] ?? null;
}
